// src/constants/core.ts
// ============================================================================
// SSOT — Core constants, types & helpers (NO UI copy; NO i18n)
// ============================================================================

import React from 'react';
import type { IconBaseProps } from 'react-icons';
import {
  FaPhoneAlt,
  FaWhatsapp,
  FaEnvelope,
  FaInstagram,
  FaFacebookF,
  FaMapMarkedAlt,
  FaRing,
  FaVest,
  FaSynagogue,
  FaGlassCheers,
  FaVolumeUp,
  FaLightbulb,
  FaStar,
  FaMicrophone,
  FaChevronLeft,
} from 'react-icons/fa';

import type { SeoCity, EventType, Region } from './geo';

// ─────────────────────────────────────────────────────────────
// Base URLs / Flags
// ─────────────────────────────────────────────────────────────

export const BASE_URL =
  (process.env.BASE_URL || 'https://www.djeliormor.co.il').replace(/\/+$/, '');

export const SITE_ORIGIN = BASE_URL.replace(/\/$/, '');
export const BUSINESS_ID_ABS = `${SITE_ORIGIN}#business`;

export const OG_IMAGE_REL = '/logo512.png' as const;
export const OG_IMAGE_ABS = `${BASE_URL}${OG_IMAGE_REL}` as const;
export const OG_PRIMARY_IMAGE = `${BASE_URL}/1200.jpg` as const;

export const INCLUDE_SPA = process.env.GENERATE_PRERENDER === '1';
export const PROD = process.env.NODE_ENV === 'production';

// Decide if SPA bundle should be injected into prerender build
export const WANT_SPA_BUNDLE =
  process.env.GENERATE_PRERENDER === '1' &&
  process.env.INCLUDE_SPA_BUNDLE === '1';

export const IS_PROD = PROD || INCLUDE_SPA;

export const PRERENDER_PREVIEW = process.env.PRERENDER_PREVIEW === '1';

export const BUCKET_URL =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com' as const;

export const DATE_PUBLISHED = '2025-08-27T08:00:00.000Z';
export const DATE_MODIFIED =
  process.env.SEO_DATE_MODIFIED || '2026-06-09T00:00:00Z';


export function normalizeSiteBase(raw: string) {
  try {
    const u = new URL(raw);
    return u.origin.replace(/\/+$/, '');
  } catch {
    if (typeof window !== 'undefined' && (window as any).location?.origin) {
      return (window as any).location.origin.replace(/\/+$/, '');
    }
    return 'https://www.djeliormor.co.il';
  }
}

export const SITE_BASE = normalizeSiteBase(BASE_URL);

// ─────────────────────────────────────────────────────────────
// Robots
// ─────────────────────────────────────────────────────────────

export type RobotsDirective = 'index,follow' | 'noindex,follow';
export const ROBOTS_DIRECTIVE: RobotsDirective = IS_PROD ? 'index,follow' : 'noindex,follow';

// ─────────────────────────────────────────────────────────────
// Reviews / Links / FAQ (Types)
// ─────────────────────────────────────────────────────────────

export type ReviewSource = 'google' | 'facebook' | 'site';

export type ReviewItem = {
  id?: string;
  author: string;
  text: string;
  translations?: Partial<Record<'en' | 'ru' | 'fr', string>>;
  rating: number; // 0..5
  dateISO?: string;
  profileUrl?: string;
  source?: ReviewSource;
};

export type InternalLink = { label: string; href: string };
export type FAQItem = { q: string; a: string };

// ─────────────────────────────────────────────────────────────
// Section 4 SEO (types only; NO he keys here)
// ─────────────────────────────────────────────────────────────

export const EVENT_GROUP = {
  weddings: 'weddings',
  hennas: 'hennas',
  barBat: 'barBat',
  events: 'events',
} as const;

export type EventGroupKey = (typeof EVENT_GROUP)[keyof typeof EVENT_GROUP];

// NOTE: keys are i18n-dependent -> keep as string here
export type ServiceKey = string;

export type SectionFourSEO = {
  intros: {
    events: string;
    services: string;
  };
  contact: Record<'phone' | 'whatsapp' | 'email' | 'instagram' | 'facebook' | 'navigate', string>;
  events: Record<EventGroupKey, string>;
  services: Record<ServiceKey, string>;
};

// ─────────────────────────────────────────────────────────────
// Albums (data only)
// ─────────────────────────────────────────────────────────────

export const ALBUM = {
  weddings: 'חתונות',
  hennas: 'חינות',
  barBat: 'בר/בת מצוות',
  forum: 'פורום',
  articles: 'כתבות',
  recommendations: 'המלצות',
  bars: 'ברים',
  flyers: 'פלאיירים',
} as const;

export type AlbumName = (typeof ALBUM)[keyof typeof ALBUM];

export const albumsOrder = [
  ALBUM.weddings,
  ALBUM.forum,
  ALBUM.articles,
  ALBUM.hennas,
  ALBUM.recommendations,
  ALBUM.barBat,
  ALBUM.bars,
  ALBUM.flyers,
] as const;

// ─────────────────────────────────────────────────────────────
// Page overrides (types only)
// ─────────────────────────────────────────────────────────────

export type SectionOneOverrides = {
  title?: string;
  descriptionLines?: string[];
  imageAlt?: string;
  extraHeadings?: string[];
};

export type SectionTwoOverrides = {
  title?: string;
  description?: string;
  additionalText?: string;
  portraitAlt?: string;
  horizontalAlt?: string;
  extraHeadings?: string[];
};

export type SectionThreeOverrides = {
  defaultAlbum?: AlbumName;
  extraHeadings?: string[];
};

export type SectionFourOverrides = {
  title?: string;
  description?: string;
  whatsappPrefill?: string;
  linksHeading?: string;
  links?: InternalLink[];
  groupTitles?: {
    contact: string;
    events: string;
    services: string;
    links: string;
  };
  seo?: SectionFourSEO;
};

export type ResponsiveSubtitleFn = (params: { isDesktop: boolean }) => string;

export type SectionFiveOverrides = {
  title?: string;
  subtitle?: string | ResponsiveSubtitleFn;
  h3Headings?: string[];
  faqs?: FAQItem[];
  extraImage?: { src: string; alt: string };
  linksHeading?: string;
  links?: InternalLink[];
};

export type SectionSixOverrides = {
  title?: string;
  subtitle?: string;
  showAggregate?: boolean;
  reviews: ReviewItem[];
  linksHeading?: string;
  links?: InternalLink[];
};

export type PlanningItem = {
  title: string;
  description: string;
  href: string;
  image: string;
  imageSrcSet?: string;
  imageSizes?: string;
  imageAlt: string;
};

export type SectionSevenOverrides = {
  title?: string;
  subtitle?: string;
  items?: PlanningItem[];
  readMoreLabel?: string;
  previousLabel?: string;
  nextLabel?: string;
  carouselLabel?: string;
};

export type PageOverrides = {
  section1?: SectionOneOverrides;
  section2?: SectionTwoOverrides;
  section3?: SectionThreeOverrides;
  section4?: SectionFourOverrides;
  section5?: SectionFiveOverrides;
  section6?: SectionSixOverrides;
  section7?: SectionSevenOverrides;
};

// ─────────────────────────────────────────────────────────────
// Utilities (generic)
// ─────────────────────────────────────────────────────────────

export const withBet = (city?: string) => {
  const c = String(city ?? '').trim();
  return c ? ` ב${c}` : '';
};

export const withBetNoLeadSpace = (city?: string) => {
  const c = String(city ?? '').trim();
  return c ? `ב${c}` : '';
};

export const escapeRegex = (s: string) => s.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');

export const stripOldOpeners = (text: string, city: SeoCity) => {
  if (!city || city.trim() === '') return text.trim();
  const c = escapeRegex(city);
  const patterns = [
    new RegExp(`^\\s*דיג[׳']?יי[^.]*${c}[.:—-]?\\s*`, 'u'),
    new RegExp(`^\\s*תקליטן[^.]*${c}[.:—-]?\\s*`, 'u'),
  ];
  let res = text.trim();
  for (const p of patterns) res = res.replace(p, '');
  return res.trim();
};

// ─────────────────────────────────────────────────────────────
// SEO services (domain level — values are stable; copy-specific mapping can live in i18n)
// ─────────────────────────────────────────────────────────────

export const SEO_SERVICE = {
  wedding: 'חתונה',
  henna: 'חינה',
  barBat: 'בר-מצווה',
  event: 'אירוע',
} as const;

export type SeoService = (typeof SEO_SERVICE)[keyof typeof SEO_SERVICE];

export const serviceByType: Record<EventType, SeoService> = {
  חתונה: SEO_SERVICE.wedding,
  חינה: SEO_SERVICE.henna,
  'בר-מצווה': SEO_SERVICE.barBat,
  אירוע: SEO_SERVICE.event,
} as const;

export function makeMetaDescriptionForType(
  typeOrService: EventType | SeoService,
  location?: SeoCity | Region | ''
): string {
  const service: SeoService =
    (typeOrService as EventType) in serviceByType
      ? serviceByType[typeOrService as EventType]
      : (typeOrService as SeoService);

  const loc = String(location ?? '').trim();
  const locText = loc ? ` ${withBet(loc)}` : '';

  if (service === SEO_SERVICE.wedding) {
    return `דיג׳יי לחתונה${locText} עם מעל 15 שנות ניסיון, פסקול אישי לזוג, קריאת קהל חדה, מיקסים מדויקים וליווי מוזיקלי מלא מהחופה ועד הרחבה.`;
  }

  if (service === SEO_SERVICE.henna) {
    return `דיג׳יי לחינה${locText} עם שילוב מדויק של מוזיקה מסורתית, מקצבים עכשוויים, אנרגיה משפחתית וליווי מקצועי לכל שלבי החגיגה.`;
  }

  if (service === SEO_SERVICE.barBat) {
    return `דיג׳יי לבת או בר מצווה${locText} עם מוזיקה שמתאימה לנוער ולמשפחה, כניסות מרגשות, רחבה שמחה וליווי מקצועי מתחילת האירוע ועד הסיום.`;
  }

  return `דיג׳יי לאירוע${locText} עם מוזיקה מותאמת לקהל, סאונד מקצועי, קריאת רחבה בזמן אמת וליווי מוזיקלי לאירועים פרטיים ועסקיים.`;
}

export const seoBuildSlug = (keyword: string, city: SeoCity) => {
  const base = keyword
    .replace(/\s+/g, '-')
    .replace(/'/g, '')
    .replace(/[״׳"]/g, '')
    .replace(/דיג׳יי/g, 'דיג׳יי'); // no i18n-normalization here

  if (!city || String(city).trim() === '') return base;
  const cityPart = String(city).replace(/\s+/g, '-');
  return `${base}-ב${cityPart}`;
};

export const buildPagePath = (serviceKeyword: string, city: SeoCity) =>
  `/${encodeURI(seoBuildSlug(serviceKeyword, city))}/`;

// ─────────────────────────────────────────────────────────────
// Business BASE (data only) + Maps helpers
// ─────────────────────────────────────────────────────────────

export const BUSINESS_BASE = {
  phone: '+972-52-862-3221',
  email: 'eliormoor@gmail.com',

  address: {
    lat: 31.2586967,
    lng: 34.781286,
    mapUrl: 'https://maps.app.goo.gl/WVCXRGSvgMW7KKnH7',
  },

  googlePlaceId: 'ChIJl85wl0cwEUkR1v6zR5AbWAA',

  sameAs: [
    'https://www.facebook.com/DjEliorMor',
    'https://www.instagram.com/dj_elior_mor',
    'https://maps.app.goo.gl/WVCXRGSvgMW7KKnH7',
  ],
} as const;

export const GOOGLE_PLACE_URL = BUSINESS_BASE.googlePlaceId
  ? `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(BUSINESS_BASE.googlePlaceId)}`
  : undefined;

export const MAPS_FALLBACK_URL =
  BUSINESS_BASE.address.mapUrl ||
  `https://www.google.com/maps?q=${encodeURIComponent('Israel')}`;

/**
 * ✅ Needed by i18n.he.ts
 * - NO he dependency
 * - Uses BUSINESS_BASE only
 */
export function preferredMapsUrl() {
  return GOOGLE_PLACE_URL || MAPS_FALLBACK_URL;
}

export const FACEBOOK_PIXEL_ID = '1232010321378282' as const;

// ─────────────────────────────────────────────────────────────
// Canonical IDs
// ─────────────────────────────────────────────────────────────

export const SECTION_THREE_ID = 'gallery';
export const SITE_BASE_NO_SLASH = String(BASE_URL).replace(/\/$/, '');
export const GALLERY_ID_ABS = `${SITE_BASE_NO_SLASH}#${SECTION_THREE_ID}`;

// ─────────────────────────────────────────────────────────────
// Media/assets (URLs only)
// ─────────────────────────────────────────────────────────────

export const LOGO1 =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/logo/logo1.png';

export const SECTION_ONE_IMAGE_SRC =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/Section+1+Image/Elior_Mor59.jpg';

export const SECTION_ONE_IMAGE_RESIZE_SRC =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/Section+1+Image/Elior_Mor59_resize.jpg';

export const SECTION_ONE_VIDEO_URL =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/Videos/album1/1.mp4';

export const POSTER_URL =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/Section%201%20Image/4.webp';

export const SECTION_TWO_IMG =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/Section+3+Image/1.webp';

export const SECTION_TWO_IMG_MOBILE_SRC =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/Section%203%20Image/1-512.webp';

export const SECTION_TWO_HORIZONTAL_IMG =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/Section+3+Image/Elior_Mor06.webp';

export const SECTION_FIVE_DEFAULT_IMG =
  'https://djeliormorbucket.s3.eu-central-1.amazonaws.com/Section+3+Image/4.webp';

// ─────────────────────────────────────────────────────────────
// Video flags (SSOT)
// ─────────────────────────────────────────────────────────────

export const INCLUDE_SSR_VIDEO = process.env.SSR_VIDEO_FOR_BOTS === '1';
export const ONLY_PRIMARY_CITIES = process.env.ONLY_PRIMARY_CITIES === '1';
export const VIDEO_DURATION_SECONDS = 30;
export const VIDEO_PUBLICATION_ISO = '2025-01-14T08:00:00+03:00';

// ─────────────────────────────────────────────────────────────
// Icons
// ─────────────────────────────────────────────────────────────

export const ICONS: Record<string, React.FC<IconBaseProps>> = {
  phone: FaPhoneAlt as unknown as React.FC<IconBaseProps>,
  whatsapp: FaWhatsapp as unknown as React.FC<IconBaseProps>,
  email: FaEnvelope as unknown as React.FC<IconBaseProps>,
  instagram: FaInstagram as unknown as React.FC<IconBaseProps>,
  facebook: FaFacebookF as unknown as React.FC<IconBaseProps>,
  navigate: FaMapMarkedAlt as unknown as React.FC<IconBaseProps>,
  ring: FaRing as unknown as React.FC<IconBaseProps>,
  vest: FaVest as unknown as React.FC<IconBaseProps>,
  synagogue: FaSynagogue as unknown as React.FC<IconBaseProps>,
  glass: FaGlassCheers as unknown as React.FC<IconBaseProps>,
  volume: FaVolumeUp as unknown as React.FC<IconBaseProps>,
  light: FaLightbulb as unknown as React.FC<IconBaseProps>,
  star: FaStar as unknown as React.FC<IconBaseProps>,
  mic: FaMicrophone as unknown as React.FC<IconBaseProps>,
  arrow: FaChevronLeft as unknown as React.FC<IconBaseProps>,
};

export type ContactButtonKey = 'phone' | 'whatsapp' | 'email' | 'instagram' | 'facebook' | 'navigate';

export type ContactButtonConfig = Readonly<{
  label: string;
  href: string;
  prefill?: string;
}>;

export type SectionButtonConfig = Readonly<{
  label: string;
  icon: keyof typeof ICONS;
  prefill?: string;
}>;


export const PRIMARY_KEYWORD_BY_SERVICE = {
  חתונה: 'די ג׳יי לחתונה',
  חינה: 'די ג׳יי לחינה',
  'בר-מצווה': 'די ג׳יי לבר מצווה',
  אירוע: 'די ג׳יי לאירוע',
} as const;


export type EventTypeKey = keyof typeof serviceByType;

export const SEO_SERVICE_WEDDING = SEO_SERVICE.wedding;
export const SEO_SERVICE_HENNA = SEO_SERVICE.henna;
export const SEO_SERVICE_BAR_BAT = SEO_SERVICE.barBat;
export const SEO_SERVICE_EVENT = SEO_SERVICE.event;

export const SECTION_THREE_DEFAULT_ALBUM = 'חתונות'
export const DEFAULT_OG_IMAGE_ABS = `${BASE_URL}${OG_IMAGE_REL}`;
